import { type ReactNode, useId, memo } from 'react';

import { assignInlineVars } from '@vanilla-extract/dynamic';
import { AnimatePresence, motion } from 'motion/react';

import { useToggle } from '@hooks';
import { Above, ActionButton, colors, DotsThreeIcon, LineSegmentIcon, typography, Text, GoalIcon } from '@ui';

import * as css from './ModuleBase.css';

type AvailableIcons = 'skeleton' | 'dots' | 'goal';

export type ModuleBaseButton = {
    label: string;
    onClick: () => void;
    icon?: AvailableIcons;
    isActive?: boolean;
    variant?: 'primary' | 'secondary';
};

type ModuleBaseProps = {
    children: ReactNode;
    title?: string;
    subtitle?: string | ReactNode;
    buttons?: ModuleBaseButton[];
    isStatic?: boolean;
    options?: React.ReactNode;
    isPending?: boolean;
    isClosed?: boolean;
    key?: string;
    button?: ReactNode;
};

const getIcon = (iconName: AvailableIcons, color: string) => {
    switch (iconName) {
        case 'goal': {
            return <GoalIcon color={color} size={16} />;
        }
        case 'skeleton': {
            return <LineSegmentIcon color={color} size={16} />;
        }
        case 'dots': {
            return <DotsThreeIcon color={color} size={16} />;
        }
    }
};

function ModuleBaseComponent({
    key,
    children,
    title,
    subtitle,
    buttons,
    isStatic,
    isPending,
    button,
    isClosed,
}: ModuleBaseProps) {
    const id = useId();
    const [isOpen, toggle] = useToggle(false);
    const shouldOpen = (isStatic || isOpen) && !isPending;

    const isInteractive = !isPending && !isStatic && !isClosed;

    return (
        <div
            key={key}
            className={css.frame({ isInteractive })}
            style={assignInlineVars({
                [css.borderColor]: colors.bluegray[200],
            })}
        >
            <div className={css.top} role="button" onClick={() => isInteractive && toggle()}>
                <div className={css.topLeft}>
                    <div className={css.header}>
                        <div className={css.heading}>
                            <Text variant="h3">{title ?? 'Unnamed Module'}</Text>
                        </div>
                        {subtitle && typeof subtitle === 'string' ? <Text variant="h4">{subtitle}</Text> : subtitle}
                    </div>

                    <Above bp="md">
                        <div className={css.topButtons}>
                            {!isPending &&
                                Array.isArray(buttons) &&
                                buttons.map((button) => {
                                    return (
                                        <button
                                            key={button.label}
                                            className={css.topButton({
                                                selected: !!button?.isActive,
                                                variant: button.variant ?? 'primary',
                                            })}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                button.onClick();
                                            }}
                                            disabled={!shouldOpen}
                                        >
                                            {button?.icon &&
                                                getIcon(
                                                    button?.icon,
                                                    button?.isActive ? colors.blue[600] : colors.bluegray[600],
                                                )}
                                            <p
                                                className={typography({
                                                    variant: 'h4',
                                                })}
                                            >
                                                {button.label}
                                            </p>
                                        </button>
                                    );
                                })}
                        </div>
                    </Above>
                </div>
                <div className={css.actions}>
                    {button}
                    {isInteractive && (
                        <ActionButton buttonSize="small" icon={isOpen ? 'collapse' : 'expand'} iconSize="large" />
                    )}
                </div>
            </div>

            <AnimatePresence initial={false}>
                {shouldOpen && !isClosed && (
                    <motion.div
                        key={id}
                        initial="collapsed"
                        animate="open"
                        exit="collapsed"
                        className={css.content}
                        variants={{
                            open: { height: 'auto', overflow: 'visible' },
                            collapsed: { height: 0, overflow: 'hidden' },
                        }}
                        transition={{
                            duration: 0.3,
                            type: 'tween',
                        }}
                    >
                        {children}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export const ModuleBase = memo(ModuleBaseComponent);
